import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Edit3, X, Save, Filter, CheckSquare, Square, ArrowUp, ArrowDown, GripVertical, ImagePlus } from 'lucide-react';
import api, { resolveUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';
import BulkImageUpload from '../../components/admin/BulkImageUpload';

const EMPTY = { name: '', description: '', price: '', category: '', image: '', available: true, pst: false, pstAmount: '', pstAmountFrom: '', modifierGroupIds: [] };

const PST_HINTS = ['drink', 'beverage', 'soda', 'pop', 'juice', 'water', 'coffee', 'tea', 'shake', 'smoothie', 'can', 'bottle', 'cold'];
const inferPst = (category) => PST_HINTS.some((h) => (category || '').toLowerCase().includes(h));

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState(params.get('category') || 'All');
  const [selected, setSelected] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkUpload, setBulkUpload] = useState(false);
  const [dragging, setDragging] = useState(null); // id being dragged
  const [dragOver, setDragOver] = useState(null); // id currently under drag
  const { toast } = useToast();

  const load = () => Promise.all([
    api.get('/menu'), api.get('/modifier-groups'), api.get('/categories')
  ]).then(([m, mg, cat]) => {
    setItems(m.data);
    setModifierGroups(mg.data || []);
    setCategories(cat.data || []);
  });
  useEffect(() => { load(); }, []);

  useEffect(() => { setParams(filter === 'All' ? {} : { category: filter }); }, [filter, setParams]);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const startNew = () => {
    const cat = filter !== 'All' ? filter : (categories[0]?.name || '');
    setEditing('new');
    setForm({ ...EMPTY, category: cat, pst: inferPst(cat) });
  };
  const startEdit = (i) => { setEditing(i.id); setForm({ ...i, price: String(i.price), modifierGroupIds: i.modifierGroupIds || [] }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const toggleMg = (gid) => setForm((f) => ({
    ...f, modifierGroupIds: f.modifierGroupIds.includes(gid) ? f.modifierGroupIds.filter((x) => x !== gid) : [...f.modifierGroupIds, gid],
  }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price),
      pstAmountFrom: form.pst && form.pstAmountFrom ? form.pstAmountFrom : null,
      pstAmount: form.pst && !form.pstAmountFrom && form.pstAmount !== '' && form.pstAmount !== null ? parseFloat(form.pstAmount) : null };
    try {
      if (editing === 'new') await api.post('/admin/menu', payload);
      else await api.put(`/admin/menu/${editing}`, payload);
      toast({ title: 'Saved', description: form.name });
      cancel(); load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed' });
    }
  };

  const del = async (i) => {
    if (!window.confirm(`Delete "${i.name}"?`)) return;
    await api.delete(`/admin/menu/${i.id}`);
    load();
  };

  const filtered = filter === 'All' ? items : items.filter((i) => i.category === filter);
  const toggleSel = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const selectAll = () => setSelected(new Set(filtered.map((i) => i.id)));
  const clearSel = () => setSelected(new Set());

  const doBulkAssign = async () => {
    if (!bulkTarget || selected.size === 0) return;
    try {
      const r = await api.post('/admin/menu/bulk-assign-category', { menuItemIds: Array.from(selected), category: bulkTarget });
      toast({ title: 'Reassigned', description: `${r.data.updated} item(s) moved to "${bulkTarget}".` });
      clearSel(); setBulkTarget(''); load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Bulk update failed' });
    }
  };

  // --- Reorder within a category ---
  const persistOrder = async (category, orderedIds) => {
    try {
      await api.post('/admin/menu/reorder', { category, orderedIds });
      // optimistic UI: patch sortOrder in local state
      setItems((prev) => prev.map((it) => {
        if (it.category !== category) return it;
        const idx = orderedIds.indexOf(it.id);
        return idx === -1 ? it : { ...it, sortOrder: idx };
      }));
    } catch (err) {
      toast({ title: 'Reorder failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const move = (item, dir) => {
    const catList = items
      .filter((i) => i.category === item.category)
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
    const idx = catList.findIndex((i) => i.id === item.id);
    const newIdx = idx + (dir === 'up' ? -1 : 1);
    if (newIdx < 0 || newIdx >= catList.length) return;
    const reordered = [...catList];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    persistOrder(item.category, reordered.map((i) => i.id));
  };

  // Sort filtered by sortOrder for display so ordering is always visible.
  const displayList = useMemo(() => {
    return filtered.slice().sort((a, b) => {
      if (filter === 'All') {
        // group by category alphabetically, then sortOrder
        const c = (a.category || '').localeCompare(b.category || '');
        if (c !== 0) return c;
      }
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);
    });
  }, [filtered, filter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Menu Items</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} items · manage categories on the <a href="/admin/categories" className="text-[#ED2E2E] font-semibold">Categories</a> page.</p>
        </div>
        <button onClick={startNew} className="btn-wrappus text-sm" data-testid="admin-menu-add"><Plus size={16} /> Add Item</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => setBulkUpload(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0F1C2E] text-white text-sm font-semibold hover:bg-[#ED2E2E] transition" data-testid="admin-menu-bulk-upload">
          <ImagePlus size={16} /> Bulk Image Upload
        </button>
        <span className="text-xs text-gray-500">Drop many photos at once — we'll auto-match to items by filename.</span>
      </div>

      {/* Filter + bulk toolbar */}
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <select value={filter} onChange={(e) => { setFilter(e.target.value); clearSel(); }} data-testid="admin-menu-filter"
                  className="text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white font-semibold focus:outline-none focus:border-[#ED2E2E]">
            <option value="All">All Categories ({items.length})</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name} ({items.filter((i) => i.category === c.name).length})</option>)}
          </select>
          {filter !== 'All' && (
            <span className="text-xs text-gray-500">Use ↑ ↓ or drag <GripVertical size={12} className="inline" /> to reorder items</span>
          )}
        </div>
        {selected.size > 0 ? (
          <>
            <span className="text-sm font-semibold text-[#ED2E2E]">{selected.size} selected</span>
            <span className="text-sm text-gray-500">Move to:</span>
            <select value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} className="text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#ED2E2E]">
              <option value="">Choose category…</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <button onClick={doBulkAssign} disabled={!bulkTarget} className="btn-wrappus text-xs px-3 py-1.5 disabled:opacity-50">Apply</button>
            <button onClick={clearSel} className="text-sm text-gray-500 hover:text-[#ED2E2E]">Clear</button>
          </>
        ) : filtered.length > 0 && (
          <button onClick={selectAll} className="text-sm text-gray-500 hover:text-[#ED2E2E] flex items-center gap-1"><Square size={14} /> Select all visible</button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-2xl p-6 shadow-sm mb-6 space-y-4" data-testid="admin-menu-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">{editing === 'new' ? 'New Menu Item' : 'Edit Menu Item'}</h2>
            <button type="button" onClick={cancel} className="p-2 hover:bg-gray-100 rounded"><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Name *"><input required value={form.name} onChange={on('name')} className="inp" data-testid="admin-menu-name" /></F>
            <F label="Category *">
              <select required value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, pst: editing === 'new' ? inferPst(e.target.value) : f.pst }))} className="inp">
                <option value="">Select category…</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </F>
            <F label="Price (CAD) *"><input required type="number" step="0.01" value={form.price} onChange={on('price')} className="inp" data-testid="admin-menu-price" /></F>
            <F label="Description" full><textarea rows={2} value={form.description} onChange={on('description')} className="inp resize-none" /></F>
            <F label="Available" full><label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={form.available} onChange={on('available')} className="accent-[#ED2E2E]" /> Show on public menu</label></F>
            <F label="Taxes" full>
              <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={!!form.pst} onChange={on('pst')} className="accent-[#ED2E2E]" data-testid="admin-menu-pst" /> Charge 7% PST as well as 5% GST (drinks)</label>
              {form.pst && (
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  <input type="number" step="0.01" min="0" placeholder="Fixed PST amount (blank = full price)"
                         disabled={!!form.pstAmountFrom}
                         value={form.pstAmountFrom ? '' : (form.pstAmount ?? '')} onChange={on('pstAmount')}
                         className="inp disabled:bg-gray-100 disabled:text-gray-400" data-testid="admin-menu-pst-amount" />
                  <select value={form.pstAmountFrom || ''} onChange={on('pstAmountFrom')} className="inp" data-testid="admin-menu-pst-follow">
                    <option value="">Or follow another item's price…</option>
                    {items.map((it) => <option key={it.id} value={it.id}>{it.name} (${it.price.toFixed(2)})</option>)}
                  </select>
                  <p className="text-xs text-gray-500 sm:col-span-2">For combos, charge PST on the drink portion only — either type the amount or pick the drink so it always follows that item's price.</p>
                </div>
              )}
            </F>
            <F label="Item Image" full>
              <ImageUploader value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="" hint="Recommended 4:3 ratio, PNG/JPG up to 8 MB." />
            </F>
            <F label={`Modifier Groups (${form.modifierGroupIds.length} selected)`} full>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1 max-h-56 overflow-y-auto p-3 bg-[#FDF3EC] rounded-xl">
                {modifierGroups.length === 0 ? (
                  <div className="col-span-full text-sm text-gray-500 text-center py-2">No modifier groups yet. Create them under <a href="/admin/modifiers" className="text-[#ED2E2E] font-semibold">Modifiers</a>.</div>
                ) : modifierGroups.map((g) => (
                  <label key={g.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition ${form.modifierGroupIds.includes(g.id) ? 'bg-[#ED2E2E] text-white' : 'bg-white text-[#0F1C2E] hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={form.modifierGroupIds.includes(g.id)} onChange={() => toggleMg(g.id)} className="accent-white" />
                    <span className="font-medium">{g.name}</span>
                  </label>
                ))}
              </div>
            </F>
          </div>
          <button type="submit" className="btn-wrappus text-sm" data-testid="admin-menu-save"><Save size={16} /> Save</button>
          <style>{`.inp{width:100%;border-radius:0.75rem;border:1px solid #E5E7EB;background:white;padding:0.65rem 0.9rem;font-size:0.9rem;} .inp:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FDF3EC] text-[#0F1C2E]">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="text-left px-3 py-3 w-16">Order</th>
                <th className="w-6 px-2 py-3"></th>
                <th className="text-left px-5 py-3">Item</th>
                <th className="text-left px-5 py-3">Category</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Modifiers</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {displayList.map((i, idx) => {
                // Determine idx bounds within category for enabling up/down
                const catList = items.filter((x) => x.category === i.category)
                  .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
                const posInCat = catList.findIndex((x) => x.id === i.id);
                const canUp = posInCat > 0;
                const canDown = posInCat < catList.length - 1;
                // Drag-and-drop handlers — only allow when filtered to a single category
                const dragEnabled = filter !== 'All';
                const isDragging = dragging === i.id;
                const isDragTarget = dragOver === i.id && dragging && dragging !== i.id;
                const onDragStart = (e) => {
                  if (!dragEnabled) { e.preventDefault(); return; }
                  setDragging(i.id);
                  try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', i.id); } catch (_) {}
                };
                const onDragEnter = (e) => {
                  if (!dragEnabled || !dragging || dragging === i.id) return;
                  e.preventDefault();
                  setDragOver(i.id);
                };
                const onDragOver = (e) => { if (dragEnabled && dragging && dragging !== i.id) e.preventDefault(); };
                const onDrop = (e) => {
                  if (!dragEnabled || !dragging || dragging === i.id) return;
                  e.preventDefault();
                  const fromIdx = catList.findIndex((x) => x.id === dragging);
                  const toIdx = catList.findIndex((x) => x.id === i.id);
                  if (fromIdx === -1 || toIdx === -1) { setDragging(null); setDragOver(null); return; }
                  const arr = [...catList];
                  const [moved] = arr.splice(fromIdx, 1);
                  arr.splice(toIdx, 0, moved);
                  persistOrder(i.category, arr.map((x) => x.id));
                  setDragging(null); setDragOver(null);
                };
                const onDragEnd = () => { setDragging(null); setDragOver(null); };
                return (
                  <tr key={i.id}
                      draggable={dragEnabled}
                      onDragStart={onDragStart}
                      onDragEnter={onDragEnter}
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                      onDragEnd={onDragEnd}
                      data-testid={`admin-menu-row-${i.id}`}
                      className={`border-t border-gray-50 hover:bg-gray-50/50 transition ${isDragging ? 'opacity-40' : ''} ${isDragTarget ? 'bg-[#FDF3EC]' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSel(i.id)}>
                        {selected.has(i.id) ? <CheckSquare size={16} className="text-[#ED2E2E]" /> : <Square size={16} className="text-gray-400" />}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => move(i, 'up')} disabled={!canUp}
                                data-testid={`admin-menu-up-${i.id}`}
                                className={`p-1 rounded ${canUp ? 'hover:bg-gray-200 text-[#0F1C2E]' : 'text-gray-300 cursor-not-allowed'}`}
                                title="Move up in category">
                          <ArrowUp size={14} />
                        </button>
                        <button onClick={() => move(i, 'down')} disabled={!canDown}
                                data-testid={`admin-menu-down-${i.id}`}
                                className={`p-1 rounded ${canDown ? 'hover:bg-gray-200 text-[#0F1C2E]' : 'text-gray-300 cursor-not-allowed'}`}
                                title="Move down in category">
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      <span title={dragEnabled ? 'Drag to reorder' : 'Filter by category to enable drag'} className={dragEnabled ? 'cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#ED2E2E]' : 'text-gray-200 cursor-not-allowed'}>
                        <GripVertical size={16} />
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {i.image ? <img src={resolveUrl(i.image)} alt={i.name} className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-[#FDF3EC]" />}
                        <div><div className="font-semibold text-[#0F1C2E]">{i.name}</div><div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{i.description}</div></div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-700">{i.category}</td>
                    <td className="px-5 py-3 font-semibold text-[#ED2E2E]">${i.price.toFixed(2)}
                      {i.pst && <span className="ml-2 align-middle text-[10px] font-bold text-[#0F1C2E] bg-[#FDF3EC] border border-[#ED2E2E]/20 rounded px-1.5 py-0.5" title={i.pstAmount ? `7% PST charged on $${i.pstAmount.toFixed(2)} of this item` : '7% PST charged on top of 5% GST'}>PST{i.pstAmount ? ` $${i.pstAmount.toFixed(2)}` : ''}</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600">{i.modifierGroupIds?.length ? `${i.modifierGroupIds.length} group${i.modifierGroupIds.length !== 1 ? 's' : ''}` : <span className="text-gray-400">None</span>}</td>
                    <td className="px-5 py-3">{i.available ? <span className="text-green-600 font-semibold text-xs">Available</span> : <span className="text-gray-400 text-xs">Hidden</span>}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(i)} className="p-2 hover:bg-gray-100 rounded text-gray-500 hover:text-[#ED2E2E]" data-testid={`admin-menu-edit-${i.id}`}><Edit3 size={16} /></button>
                      <button onClick={() => del(i)} className="p-2 hover:bg-red-50 rounded text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })}
              {displayList.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-8 text-center text-gray-400">No items in this category</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BulkImageUpload open={bulkUpload} onClose={() => setBulkUpload(false)} items={items} onApplied={load} />
    </div>
  );
}

function F({ label, children, full }) {
  return <div className={full ? 'md:col-span-2' : ''}><label className="text-xs font-semibold text-gray-600 uppercase block mb-1">{label}</label>{children}</div>;
}
